import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StorageService } from './storage.service';
import { environment } from '~environments/environment';
import { JWTToken, JWTTokenAPI } from '../models/authentication/jwt-token';


/**
 * This service is responsible for user log in and log out.
 */
@Injectable()
export class AuthenticationService {
  public API_URL: string = environment.envAPI;
  constructor(
    private http: HttpClient,
    private storage: StorageService,
    @Inject(LOCALE_ID) public locale: string
  ) { }

  /**
   * Login the user if the credentials are correct.
   */
  public login(username: string, password: string): Observable<JWTToken> {
    return this.http.post<JWTToken>(`${this.API_URL}/login`, { _username: username, _password: password })
      .pipe(map((jwtToken: JWTToken) => this.configureToken(jwtToken)));
  }

  /**
   * Get a new access token, with the refresh token.
   */
  public refresh(refreshToken: string): Observable<JWTToken> | null {
    return this.http.post<JWTToken>(`${this.API_URL}/token/refresh`, { refresh_token: refreshToken })
      .pipe(map((jwtToken: JWTToken) => this.configureToken(jwtToken)));
  }

  /**
   * Create the JWT Token from external login.
   */
  public createJWTFromExternalJWTToken(externalJWTToken: string): JWTToken | null {
    let jwtToken = null;

    if (externalJWTToken !== null) {
      externalJWTToken = JSON.parse(externalJWTToken);

      if (externalJWTToken['token'] !== null) {
        const decodedToken = this.parseJwt(externalJWTToken['token']);

        if (decodedToken === null || decodedToken.exp === null || decodedToken.iat === null) {
          throw new Error('Bad token provided');
        }

        jwtToken = new JWTToken(externalJWTToken['token'], externalJWTToken['refresh_token']);
        jwtToken.setExpireAt = decodedToken;
        jwtToken.setIssuedAt = decodedToken;
        jwtToken.setForcePasswordChange = decodedToken;

        this.storage.setToken(jwtToken);
      }
    }

    return jwtToken;
  }

  /**
   * Parse the returned JWT token into a readable format.
   */
  public parseJwt(token: string): JWTTokenAPI | null {
    if (token.indexOf('.') === -1 || token.indexOf('-') === -1 || token.indexOf('_') === -1) {
      return null;
    }

    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    return JSON.parse(window.atob(base64));
  }

  /**
   * Log out the user.
   */
  public logout(): void {
    // remove user from local storage to log user out
    localStorage.removeItem('authenticatedUser');
    localStorage.removeItem('token');
    this.storage.removeAuthenticatedUser();
    this.storage.removeCountries();
    this.storage.clearSelectedClub();
  }

  /**
   * Reset the password.
   */
  public passwordReset(password: any): any {
    return this.http.post(`${this.API_URL}/forced-password-changes`, password);
  }

  /**
   * Check if the user is logged in.
   */
  public isLoggedIn(): boolean {
    return localStorage.getItem('authenticatedUser') !== null;
  }

  /**
   * Get the currently logged-in user.
   */
  public fetchAuthenticatedUser(): Observable<any> {
    return this.http.get<any>(`${environment.envAPI}/me`);
  }

  /**
   * Get the currently logged-in user.
   */
  public fetchOpenReminders(clubId: number): Observable<any> {
    return this.http.get<any>(`${environment.envAPI}/me/open-reminder-count?club=${clubId}`);
  }

  public configureToken(jwtToken: JWTToken) {
    if (jwtToken != null && jwtToken.token != null) {
      const decodedToken = this.parseJwt(jwtToken.token);

      if (decodedToken.exp === null || decodedToken.iat === null) {
        throw new Error('Bad token provided');
      }

      jwtToken = new JWTToken(jwtToken.token, jwtToken['refresh_token']);
      jwtToken.setExpireAt = decodedToken;
      jwtToken.setIssuedAt = decodedToken;
      jwtToken.setForcePasswordChange = decodedToken;

      this.storage.setToken(jwtToken);
    }
    return jwtToken;
  }
}
